/**
 * Hand-written workspace API contracts.
 *
 * PROVISIONAL. The OF.Manager team/project/permission endpoints return bare JSON
 * arrays and records that carry no response metadata in the OpenAPI document, so
 * `openapi-typescript` emits nothing for them and `generate-types` cannot derive
 * them. Everything in this file is transcribed by hand from the backend sources on
 * `OF.Manager@feature/20260925_SF-2836_MVH_contextual-storage-path-exclusivity`
 * and must be re-verified whenever those endpoints change.
 *
 * Request DTOs (CreateTeam, AssignTeamRole, ...) ARE generated - import those from
 * `@domain/schemas/BI`, not from here.
 *
 * Serialization notes that this file depends on:
 * - TheGoodFramework registers a global `JsonStringEnumConverter`, so every enum
 *   crosses the wire as a string, never an integer.
 * - Property names are camelCase.
 */

/**
`MyTeamDTO.Role` / `MePermissionsResponse.TeamRole`. Produced by `TeamRole.ToString()`.

The type is derived from this array (not the other way around) so every `Record<TeamRoleName, _>`
elsewhere - and `TEAM_ROLE_NAMES` itself, checked directly in tests - stay in sync by construction:
adding a role here is the only way to add it to the type at all.
*/
export const TeamRoles = {
  Member: "Member",
  ProjectLead: "ProjectLead",
  Manager: "Manager",
} as const;
export const TEAM_ROLE_NAMES = [TeamRoles.Member, TeamRoles.ProjectLead, TeamRoles.Manager] as const;
export type TeamRoleName = (typeof TEAM_ROLE_NAMES)[number];

/**
`MyProjectDTO.Role` / `MePermissionsResponse.ProjectRole`. Produced by `ProjectRole.ToString()`.

Derived from this array for the same reason as `TEAM_ROLE_NAMES` above.
*/
export const ProjectRoles = {
  Reader: "Reader",
  Operator: "Operator",
  Manager: "Manager",
} as const;
export const PROJECT_ROLE_NAMES = [ProjectRoles.Reader, ProjectRoles.Operator, ProjectRoles.Manager] as const;
export type ProjectRoleName = (typeof PROJECT_ROLE_NAMES)[number];

/**
 * Deployment-wide administrators are not a team/project role; the backend
 * substitutes the literal "Admin" into resolved permission roles and `MyTeamDTO.role`.
 */
export const ADMIN_ROLE_NAME = "Admin";

export type TeamRoleOrAdmin = TeamRoleName | typeof ADMIN_ROLE_NAME;
export type ProjectRoleOrAdmin = ProjectRoleName | typeof ADMIN_ROLE_NAME;

/**
`SF.Manager.Authority.Domain.TeamAction`.
*/
export const TeamActions = {
  ViewInventory: "ViewInventory",
  CreateProject: "CreateProject",
  AssignProjectRoles: "AssignProjectRoles",
  RenameProject: "RenameProject",
  RenameTeam: "RenameTeam",
  DeleteProject: "DeleteProject",
  ManageMembership: "ManageMembership",
  AssignTeamRoles: "AssignTeamRoles",
  ManageStorageConfiguration: "ManageStorageConfiguration",
  ManageSoftwareVersions: "ManageSoftwareVersions",
} as const;
export type TeamAction = (typeof TeamActions)[keyof typeof TeamActions];

/**
`SF.Manager.Authority.Domain.ProjectAction`.
*/
export const ProjectActions = {
  View: "View",
  TriggerJob: "TriggerJob",
  DeleteJobArtifacts: "DeleteJobArtifacts",
  ManageMembership: "ManageMembership",
  Rename: "Rename",
  ManageStorageConfiguration: "ManageStorageConfiguration",
  ManageSoftwareVersions: "ManageSoftwareVersions",
} as const;
export type ProjectAction = (typeof ProjectActions)[keyof typeof ProjectActions];

/**
Item of the bare array returned by `GET /teams`. Backend type `MyTeamDTO`.
*/
export interface MyTeam {
  id: string;
  name: string;
  // Structural context actions require entitlement even when this is true.
  isDefault: boolean;
  /**
  The signed-in user's own role in this team, or null if they hold none (only possible for an
  Admin, who sees every team regardless of membership).
  */
  role: TeamRoleOrAdmin | null;
}

/**
Item of the bare array returned by `GET /projects`. Backend type `MyProjectDTO`.
*/
export interface MyProject {
  id: string;
  name: string;
  teamId: string;
  // Job and management actions use this to resolve entitlement requirements.
  isDefault: boolean;
  /**
  The signed-in user's own role in this project, or null if they hold none (only possible for an
  Admin, who sees every project regardless of membership).
  */
  role: ProjectRoleName | null;
  /**
  Assigned project Managers. May be absent on older API responses or local rows awaiting a workspace refresh.
  */
  managerUserIds?: string[];
}

/**
 * Item of the bare array returned by `GET /teams/{teamId}/projects`.
 * Backend type `TeamProjectInventoryDTO`.
 *
 * Deliberately NOT interchangeable with {@link MyProject}: this is the team's
 * inventory view and says nothing about the caller's own role, so it must never be
 * used to decide what the caller may do.
 */
export interface TeamProjectInventoryItem {
  id: string;
  name: string;
  teamId: string;
  managerUserIds: string[];
}

/**
 * Response of `GET /me/permissions`. Backend type `MePermissionsResponse`.
 *
 * `teamRole`/`teamActions` are populated only when `teamId` was supplied, and
 * `projectRole`/`projectActions` only when `projectId` was supplied; the omitted
 * half comes back as `null` and `[]` respectively. Supplying neither is a 400.
 *
 * There is no entitlement field. Entitlement is applied server-side by filtering
 * mutating actions out of the arrays, so an entitlement-locked context is
 * indistinguishable here from an under-privileged one - the distinction only
 * surfaces on the denial response of an actual mutating call.
 */
export interface MePermissions {
  isAdmin: boolean;
  teamRole: TeamRoleOrAdmin | null;
  teamActions: TeamAction[];
  projectRole: ProjectRoleOrAdmin | null;
  projectActions: ProjectAction[];
}

/**
Scope selector for `GET /me/permissions`. At least one id must be present.
*/
export interface PermissionScope {
  teamId?: string;
  projectId?: string;
}

/**
`TeamMembership` entity, an item of `TeamDetail.memberships`.
*/
export interface TeamMembership {
  id: string;
  teamId: string;
  userId: string;
  role: TeamRoleName;
}

/**
 * Backend type `Team` (the domain entity, serialized directly - there is no DTO).
 * Response of `GET /teams/{teamId}`, `POST /teams`, `PUT /teams/{teamId}/name`,
 * `PUT /teams/{teamId}/members`, and `DELETE /teams/{teamId}/members/{userId}`.
 */
export interface TeamDetail {
  id: string;
  name: string;
  isDefault: boolean;
  memberships: TeamMembership[];
}

/**
`ProjectMembership` entity, an item of `ProjectDetail.memberships`.
*/
export interface ProjectMembership {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectRoleName;
}

/**
 * Backend type `Project` (the domain entity, serialized directly - there is no DTO).
 * Response of `POST /teams/{teamId}/projects`, `PUT /projects/{projectId}/name`,
 * `PUT /projects/{projectId}/members`, and `DELETE /projects/{projectId}/members/{userId}`.
 */
export interface ProjectDetail {
  id: string;
  name: string;
  teamId: string;
  isDefault: boolean;
  memberships: ProjectMembership[];
}

/**
`ProjectMoveRequestStatus.ToString()`.
*/
export type ProjectMoveRequestStatusName = "Pending" | "Accepted" | "Rejected" | "Expired" | "Withdrawn";

/**
 * Backend type `ProjectMoveRequest` (the domain entity, serialized directly).
 * Response of propose/accept/reject/withdraw and `GET /teams/{teamId}/move-requests/pending`.
 */
export interface ProjectMoveRequest {
  id: string;
  projectId: string;
  sourceTeamId: string;
  targetTeamId: string;
  proposedByUserId: string;
  proposedAtUtc: string;
  expiresAtUtc: string;
  status: ProjectMoveRequestStatusName;
}

/**
Item of `GET /users?ids=`'s bare array, and of the `list` field of `GET /users?search=`'s paginated
response (see {@link PagedUserSummaryList}). Backend type `UserSummaryDTO`.
*/
export interface UserSummary {
  id: string;
  username: string;
  displayName: string;
}

/**
Response of `GET /users?search=`, paginated like the Jobs/RawFiles/etc. listings. Backend type
`PagedListDTO<UserSummaryDTO>`.
*/
export interface PagedUserSummaryList {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  list: UserSummary[];
}
